// Employee-recorded significant dates — net-new spec surface (PO direction;
// see packages/shared/src/significant-dates.ts for the domain rationale).
//
// Mounted inside `SupportPreferencesCard`'s "Significant dates" group,
// alongside the `keyDateAwareness` consent radio. Consent-gated in the UI
// (mirrors the API's write-path gate): when the employee has chosen "I'd
// prefer not to record this", the add/edit form is hidden rather than
// disabled — showing a form that will 400 on submit is worse than not
// showing it.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SIGNIFICANT_DATE_MONTH_LABELS,
  SIGNIFICANT_DATE_TYPES,
  SIGNIFICANT_DATE_TYPE_LABELS,
  type CreateSignificantDateRequest,
  type SignificantDate,
} from '@besideu/shared/significant-dates';
import type { KeyDateAwareness } from '@besideu/shared/preferences';
import { api, ApiError } from '@/lib/api.ts';
type ApiErrorBody = { error?: string };
import { Button } from '@/components/ui/Button.tsx';
import { Field, Select, TextInput } from '@/components/ui/Field.tsx';

const EMPTY_FORM: CreateSignificantDateRequest = {
  dateType: 'loved_one_birthday',
  label: '',
  month: 1,
  day: 1,
};

export function SignificantDatesList({
  keyDateAwareness,
}: {
  keyDateAwareness: KeyDateAwareness | null;
}) {
  const queryClient = useQueryClient();
  const dates = useQuery({
    queryKey: ['employee', 'significant-dates'],
    queryFn: () => api.get<{ dates: SignificantDate[] }>('/api/employee/significant-dates'),
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CreateSignificantDateRequest>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['employee', 'significant-dates'] });

  const save = useMutation({
    mutationFn: (input: CreateSignificantDateRequest) =>
      editingId
        ? api.put<{ data: SignificantDate }>(`/api/employee/significant-dates/${editingId}`, input)
        : api.post<{ data: SignificantDate }>('/api/employee/significant-dates', input),
    onSuccess: () => {
      invalidate();
      setForm(EMPTY_FORM);
      setEditingId(null);
      setError(null);
    },
    onError: (e) =>
      setError(
        e instanceof ApiError &&
          (e.body as ApiErrorBody | null)?.error === 'key_date_awareness_declined'
          ? 'Your preference above is set to not record dates — change it first.'
          : 'Could not save. Check the date and try again.',
      ),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/api/employee/significant-dates/${id}`),
    onSuccess: invalidate,
  });

  function startEdit(d: SignificantDate) {
    setEditingId(d.id);
    setForm({ dateType: d.dateType, label: d.label, month: d.month, day: d.day });
    setError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError(null);
  }

  const set = (patch: Partial<CreateSignificantDateRequest>) =>
    setForm((f) => ({ ...f, ...patch }));

  // This block is a `<div>`, not a `<form>` (see the comment below it) —
  // nested inside `SupportPreferencesCard`'s single outer `<form>`. Without
  // this guard, pressing Enter in any of its fields (a natural action while
  // filling one in) submits THAT form via its only submit button ("Save
  // preferences"), silently discarding whatever was typed here instead of
  // adding the date. Buttons are excluded — they handle their own
  // Enter-to-click activation, which this must not interfere with.
  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Enter' || (e.target as HTMLElement).tagName === 'BUTTON') return;
    e.preventDefault();
    if (!save.isPending && form.label.trim() !== '') save.mutate(form);
  }

  if (keyDateAwareness === 'prefer_not_record') {
    return (
      <p className="text-sm text-muted-foreground">
        You've chosen not to have significant dates recorded. Change the choice above if you'd like
        to add one.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Add a birthday, anniversary or other date that may be difficult. Only HR can see the
        specific date and what it is — your manager only sees that something is coming up, so they
        can be mindful without knowing the detail.
      </p>

      {/* A `<div>`, not a nested `<form>` — this component is mounted inside
          `SupportPreferencesCard`'s own outer `<form>`, and HTML forbids
          nesting forms (the browser would otherwise silently close the outer
          one early). The "Add date" button below submits via `onClick`,
          not form submission. */}
      <div className="space-y-3 rounded-md border border-border p-4" onKeyDown={handleKeyDown}>
        <Field label="What kind of date is it?">
          {(id, descProps) => (
            <Select
              id={id}
              {...descProps}
              value={form.dateType}
              onChange={(e) =>
                set({ dateType: e.target.value as CreateSignificantDateRequest['dateType'] })
              }
            >
              {SIGNIFICANT_DATE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {SIGNIFICANT_DATE_TYPE_LABELS[t]}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field
          label="A short label for yourself"
          hint="e.g. “Mum's birthday” — only HR can see this"
        >
          {(id, descProps) => (
            // Deliberately no `required` attribute: this input lives inside
            // `SupportPreferencesCard`'s single outer `<form>` (see the file
            // header comment), collapsed inside a closed-by-default `Group`
            // disclosure. A native `required` field the browser can't focus
            // (hidden behind a closed `<details>`) silently blocks ANY
            // submit of that shared form — including the unrelated "Save
            // preferences" button — with no visible error, whenever this
            // section is left collapsed with an empty label (the default
            // state for every employee). It also never validated its own
            // "Add date" button, which is `type="button"`/`onClick`, not a
            // form submit. The `disabled` guard on that button below is the
            // real (working) requirement check.
            <TextInput
              id={id}
              {...descProps}
              maxLength={120}
              value={form.label}
              onChange={(e) => set({ label: e.target.value })}
            />
          )}
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Month">
            {(id, descProps) => (
              <Select
                id={id}
                {...descProps}
                value={form.month}
                onChange={(e) => set({ month: Number(e.target.value) })}
              >
                {SIGNIFICANT_DATE_MONTH_LABELS.map((label, i) => (
                  <option key={label} value={i + 1}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Day">
            {(id, descProps) => (
              <Select
                id={id}
                {...descProps}
                value={form.day}
                onChange={(e) => set({ day: Number(e.target.value) })}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>

        <div className="flex items-center gap-3">
          <Button
            type="button"
            disabled={save.isPending || form.label.trim() === ''}
            onClick={() => save.mutate(form)}
          >
            {save.isPending ? 'Saving…' : editingId ? 'Save changes' : 'Add date'}
          </Button>
          {editingId && (
            <Button type="button" variant="ghost" onClick={cancelEdit}>
              Cancel
            </Button>
          )}
          {error && (
            <span className="text-sm text-traffic-red" role="alert">
              {error}
            </span>
          )}
        </div>
      </div>

      {dates.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : dates.error || !dates.data ? (
        <p className="text-sm text-traffic-red">Could not load your dates.</p>
      ) : dates.data.dates.length === 0 ? (
        <p className="text-sm text-muted-foreground">No dates added yet.</p>
      ) : (
        <ul className="space-y-2">
          {dates.data.dates.map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between gap-3 rounded-md border border-border p-3"
            >
              <div>
                <p className="text-sm font-medium">{d.label}</p>
                <p className="text-xs text-muted-foreground">
                  {SIGNIFICANT_DATE_TYPE_LABELS[d.dateType]} ·{' '}
                  {SIGNIFICANT_DATE_MONTH_LABELS[d.month - 1]} {d.day}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button type="button" variant="ghost" onClick={() => startEdit(d)}>
                  Edit
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(d.id)}
                >
                  Delete
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
